// =====================================================
// TENANT CRUD API + LEASE CRUD API + VERIFICATION API + RULES & DOCUMENTS API
// =====================================================

import { z } from 'zod';
import type { LeaseState, LeaseStateFilter } from '@resida/shared';
import { apiFetch } from './api.core';
import type { TenantWithDetails, TenantInvite } from './portfolio.api';
import type {
  LeaseListResponse,
  LeaseResponse,
  CreateLeaseInput,
  UpdateLeaseInput,
  ArchiveLeaseInput,
  ScheduleMoveOutInput,
  ArchiveLeaseResult,
  LeaseDetail,
} from '../types/lease';
import {
  AddressSchema,
  IndividualVerificationRequestSchema,
  ControllerSchema,
  BusinessVerificationRequestSchema,
  AddBeneficialOwnerRequestSchema,
  RuleResponseSchema,
  DocumentResponseSchema,
} from '@resida/api-types/lease-support';

// =====================================================
// TENANT CRUD API
// =====================================================

export interface Tenant {
  id: string;
  unitId: string | null;
  portfolioId: string;
  identityId: string | null;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  status: 'pending' | 'active' | 'inactive' | 'archived';
  archivedAt: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTenantData {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  status?: 'pending' | 'active' | 'inactive';
  metadata?: Record<string, unknown>;
}

export interface UpdateTenantData {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  status?: 'pending' | 'active' | 'inactive';
  metadata?: Record<string, unknown>;
}

/**
 * List tenants for a specific unit
 */
export async function listTenantsByUnit(unitId: string): Promise<{ tenants: TenantWithDetails[] }> {
  return apiFetch<{ tenants: TenantWithDetails[] }>(`/landlord/api/units/${unitId}/tenants`);
}

/**
 * List tenants for a specific portfolio
 */
export async function listTenantsByPortfolio(portfolioId: string): Promise<{ tenants: TenantWithDetails[] }> {
  return apiFetch<{ tenants: TenantWithDetails[] }>(`/landlord/api/portfolios/${portfolioId}/tenants`);
}

/**
 * Get a single tenant by ID
 */
export async function getTenant(id: string): Promise<{ tenant: TenantWithDetails }> {
  return apiFetch<{ tenant: TenantWithDetails }>(`/landlord/api/tenants/${id}`);
}

/**
 * Create a new tenant for a unit
 */
export async function createTenant(unitId: string, data: CreateTenantData): Promise<Tenant> {
  return apiFetch<Tenant>(`/landlord/api/units/${unitId}/tenants`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update an existing tenant
 */
export async function updateTenant(id: string, data: UpdateTenantData): Promise<Tenant> {
  return apiFetch<Tenant>(`/landlord/api/tenants/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Remove a tenant (soft delete)
 */
export async function removeTenant(id: string): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>(`/landlord/api/tenants/${id}`, {
    method: 'DELETE',
  });
}

/**
 * Send an invite to a tenant
 */
export async function sendTenantInvite(
  unitId: string,
  tenantId: string,
  roles?: string
): Promise<{ invite: TenantInvite }> {
  return apiFetch<{ invite: TenantInvite }>(`/landlord/api/units/${unitId}/tenants/${tenantId}/invite`, {
    method: 'POST',
    body: JSON.stringify({ roles }),
  });
}

/**
 * Re-send a tenant invite (revokes old and creates new)
 */
export async function resendTenantInvite(tenantId: string): Promise<{ invite: TenantInvite }> {
  return apiFetch<{ invite: TenantInvite }>(`/landlord/api/tenants/${tenantId}/invite/resend`, {
    method: 'POST',
  });
}

// =====================================================
// LEASE CRUD API
// =====================================================

export interface ListLeasesParams {
  unitId: string;
  state?: LeaseStateFilter;
}

export interface LeaseWithContext {
  id: string;
  unitId: string;
  startDate: string;
  endDate: string | null;
  rentAmount: number;
  securityDeposit: number;
  state: LeaseState;
  actualEndDate: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  tenants: Array<{ id: string; firstName: string; lastName: string; email: string; phone: string | null }>;
  propertyName: string;
  unitNumber: string | null;
  address1: string;
}

export interface LeaseListAllResponse {
  leases: LeaseWithContext[];
}

/**
 * List all leases across all portfolios
 */
export async function listAllLeases(params?: { state?: LeaseStateFilter }): Promise<LeaseListAllResponse> {
  const searchParams = new URLSearchParams();
  if (params?.state) searchParams.set('state', params.state);
  const queryString = searchParams.toString();
  const endpoint = queryString ? `/landlord/api/leases?${queryString}` : '/landlord/api/leases';
  return apiFetch<LeaseListAllResponse>(endpoint);
}

/**
 * List leases for a specific unit
 */
export async function listLeases(params: ListLeasesParams): Promise<LeaseListResponse> {
  const searchParams = new URLSearchParams();
  if (params.state) searchParams.set('state', params.state);

  return apiFetch<LeaseListResponse>(
    `/landlord/api/units/${params.unitId}/leases?${searchParams.toString()}`
  );
}

/**
 * Get a single lease by ID
 */
export async function getLease(_unitId: string, leaseId: string): Promise<LeaseResponse> {
  return apiFetch<LeaseResponse>(`/landlord/api/leases/${leaseId}`);
}

/**
 * Create a new lease for a unit
 */
export async function createLease(unitId: string, data: CreateLeaseInput): Promise<LeaseResponse> {
  return apiFetch<LeaseResponse>(`/landlord/api/units/${unitId}/leases`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Update an existing lease
 */
export async function updateLease(
  _unitId: string,
  leaseId: string,
  data: UpdateLeaseInput
): Promise<LeaseResponse> {
  return apiFetch<LeaseResponse>(`/landlord/api/leases/${leaseId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * Archive a lease (sets state to archived).
 *
 * Response is the flat lease object extended with settlement context
 * (ArchiveLeaseResponseSchema, ADR-LL-020) — NOT wrapped in `{ lease }`.
 */
export async function archiveLease(
  _unitId: string,
  leaseId: string,
  data: ArchiveLeaseInput
): Promise<ArchiveLeaseResult> {
  return apiFetch<ArchiveLeaseResult>(`/landlord/api/leases/${leaseId}/archive`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Unarchive a previously archived lease.
 * Response is the flat lease object (LeaseResponseSchema) — NOT wrapped in `{ lease }`.
 */
export async function unarchiveLease(leaseId: string): Promise<LeaseDetail> {
  return apiFetch<LeaseDetail>(`/landlord/api/leases/${leaseId}/unarchive`, {
    method: 'POST',
  });
}

/**
 * Set a departure date for a specific tenant on a lease (BR-LL-06).
 * Response is the flat lease object (LeaseResponseSchema) — NOT wrapped in `{ lease }`.
 */
export async function setTenantDepartureDate(
  leaseId: string,
  tenantId: string,
  departureDate: string
): Promise<LeaseDetail> {
  return apiFetch<LeaseDetail>(`/landlord/api/leases/${leaseId}/tenants/${tenantId}/departure`, {
    method: 'POST',
    body: JSON.stringify({ departureDate }),
  });
}

/**
 * Explicitly schedule (or re-schedule) a move-out for a lease (BR-LL-08).
 * Sets endDate, moveOutScheduledAt, and an optional reason in one transaction, and
 * spawns the lease_end tracking saga on first scheduling. Rejects on an archived
 * lease (BR-LL-11). Response is the flat lease object (ScheduleMoveOutResponseSchema).
 */
export async function scheduleMoveOut(
  leaseId: string,
  data: ScheduleMoveOutInput
): Promise<LeaseDetail> {
  return apiFetch<LeaseDetail>(`/landlord/api/leases/${leaseId}/schedule-move-out`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Add an existing tenant to a lease (BYO manual leases)
 */
export async function addTenantToLease(
  leaseId: string,
  tenantId: string
): Promise<LeaseResponse> {
  return apiFetch<LeaseResponse>(`/landlord/api/leases/${leaseId}/tenants`, {
    method: 'POST',
    body: JSON.stringify({ tenantId }),
  });
}

// =====================================================
// VERIFICATION API
// =====================================================

export interface VerificationStatus {
  status: 'not_started' | 'unverified' | 'retry' | 'document' | 'suspended' | 'verified';
  customerType?: 'individual' | 'business';
  beneficialOwners?: BeneficialOwner[];
  requiresDocuments?: boolean;
  canAddFundingSource?: boolean;
}

export interface BeneficialOwner {
  id: string;
  firstName: string;
  lastName: string;
  ownershipPercentage: number;
  status: string;
}

// Derived from AddressSchema in @resida/api-types/lease-support
export type Address = z.infer<typeof AddressSchema>;

// Derived from IndividualVerificationRequestSchema in @resida/api-types/lease-support
export type IndividualVerificationInput = z.infer<typeof IndividualVerificationRequestSchema>;

// Derived from ControllerSchema in @resida/api-types/lease-support
export type BusinessController = z.infer<typeof ControllerSchema>;

// Derived from BusinessVerificationRequestSchema in @resida/api-types/lease-support
export type BusinessVerificationInput = z.infer<typeof BusinessVerificationRequestSchema>;

// Derived from AddBeneficialOwnerRequestSchema in @resida/api-types/lease-support
export type AddBeneficialOwnerInput = z.infer<typeof AddBeneficialOwnerRequestSchema>;

export interface UploadDocumentInput {
  file: File;
  documentType: 'passport' | 'license' | 'idCard' | 'other';
  beneficialOwnerId?: string;
}

/**
 * Get current verification status
 */
export async function getVerificationStatus(): Promise<VerificationStatus> {
  return apiFetch<VerificationStatus>('/landlord/api/verification/status');
}

/**
 * Start individual verification (KYC)
 */
export async function startIndividualVerification(
  data: IndividualVerificationInput
): Promise<{ success: boolean; customer: { id: string; status: string } }> {
  return apiFetch('/landlord/api/verification/individual', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Start business verification (KYB)
 */
export async function startBusinessVerification(
  data: BusinessVerificationInput
): Promise<{ success: boolean; customer: { id: string; status: string } }> {
  return apiFetch('/landlord/api/verification/business', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Add beneficial owner
 */
export async function addBeneficialOwner(
  data: AddBeneficialOwnerInput
): Promise<{ success: boolean; beneficialOwner: BeneficialOwner }> {
  return apiFetch('/landlord/api/verification/beneficial-owners', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Remove beneficial owner
 */
export async function removeBeneficialOwner(id: string): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>(`/landlord/api/verification/beneficial-owners/${id}`, {
    method: 'DELETE',
  });
}

/**
 * Certify beneficial ownership
 */
export async function certifyBeneficialOwnership(): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>('/landlord/api/verification/certify', {
    method: 'POST',
  });
}

// =====================================================
// RULES & DOCUMENTS API
// =====================================================

// Derived from RuleResponseSchema in @resida/api-types/lease-support
export type RuleResponse = z.infer<typeof RuleResponseSchema>;

// Derived from DocumentResponseSchema in @resida/api-types/lease-support
export type DocumentResponse = z.infer<typeof DocumentResponseSchema>;

export const rulesApi = {
  listForProperty: (propertyId: string) =>
    apiFetch<{ rules: RuleResponse[] }>(`/landlord/api/properties/${propertyId}/rules`),
  listForUnit: (unitId: string) =>
    apiFetch<{ rules: RuleResponse[] }>(`/landlord/api/units/${unitId}/rules`),
  update: (id: string, data: { content?: string }) =>
    apiFetch<{ rule: RuleResponse }>(`/landlord/api/rules/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
};

export const documentsApi = {
  listForProperty: (propertyId: string) =>
    apiFetch<{ documents: DocumentResponse[] }>(`/landlord/api/properties/${propertyId}/documents`),
  listForUnit: (unitId: string) =>
    apiFetch<{ documents: DocumentResponse[] }>(`/landlord/api/units/${unitId}/documents`),
  listForLease: (leaseId: string) =>
    apiFetch<{ documents: DocumentResponse[] }>(`/landlord/api/leases/${leaseId}/documents`),
  upload: (formData: FormData) =>
    apiFetch<{ document: DocumentResponse }>('/landlord/api/documents/upload', {
      method: 'POST',
      body: formData,
    }),
  update: (id: string, data: { name?: string; description?: string; tenantHidden?: boolean }) =>
    apiFetch<{ document: DocumentResponse }>(`/landlord/api/documents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    apiFetch<void>(`/landlord/api/documents/${id}`, { method: 'DELETE' }),
  downloadUrl: (id: string) => `/landlord/api/documents/${id}/download`,
  viewUrl: (id: string) => `/landlord/api/documents/${id}/download?inline=true`,
};

